// Instant Book — what a search card / the search filter can know about a
// listing's EFFECTIVE booking mode.
//
// The hub owns the flag (users.bookingMode / users.instantBook = partner
// default, xdata.bookingMode / xdata.instantBook = per-listing override) and
// resolves the effective value server-side, because the FE never sees the
// partner/account doc. Search cards carry the result as:
//   xdata.bookingModeEffective : 'enquiry' | 'instant_confirmation' | 'instant_book'
//   xdata.instantBookEffective : boolean (instant_book only — the legacy twin)
// Older payloads (a hub not yet on the merged-search attach) carry neither; the
// raw listing fields are then the best available signal, and "no signal" reads
// as null (unknown), NOT as "not instant" — the filter must never drop a card
// it cannot classify.
//
// What Villa Tracker SELLS instantly is instant_book only: /reserve offers
// "Book & Pay Now" for instantBookEffective === true, and VT-Backend's
// pre-charge check (#85) refuses any other mode. An instant_confirmation listing
// is booked here like a request (the 48-hour card hold, or a request the team
// confirms), so it gets no instant badge and does not pass the "Instant Book
// only" filter. (smilinghouse.com counts both modes; this site cannot sell
// the second one instantly.)

export const INSTANT_MODES = ["instant_book"];
const MODES = ["instant_book", "instant_confirmation", "enquiry"];

const isMode = (v) => typeof v === "string" && MODES.includes(v);

const xdataOf = (input) => {
  if (!input || typeof input !== "object") return null;
  if (input.xdata && typeof input.xdata === "object") return input.xdata;
  // PropertyBox receives xdata as its own prop; callers may hand it straight in.
  if ("bookingModeEffective" in input || "instantBookEffective" in input || "bookingMode" in input || "instantBook" in input) return input;
  return null;
};

/** Effective booking mode of a search result, or null when the payload gives
 *  no usable signal. Accepts a card ({listing, xdata}), a listing, or an xdata. */
export function bookingModeOf(input) {
  const x = xdataOf(input);
  if (!x) return null;
  if (isMode(x.bookingModeEffective)) return x.bookingModeEffective;
  if (typeof x.instantBookEffective === "boolean") return x.instantBookEffective ? "instant_book" : "enquiry";
  // Pre-attach payload: the listing's OWN override is all we have. An absent /
  // null override means "inherit the partner default", which we cannot see.
  if (isMode(x.bookingMode)) return x.bookingMode;
  if (typeof x.instantBook === "boolean") return x.instantBook ? "instant_book" : "enquiry";
  return null;
}

/** true = instant_book, false = request / hold (instant_confirmation and
 *  enquiry), null = unknown. */
export function isInstantListing(input) {
  const mode = bookingModeOf(input);
  return mode === null ? null : INSTANT_MODES.includes(mode);
}

/** Card badge copy per mode; no entry = no badge. Only instant_book is sold
 *  instantly here; instant_confirmation is a request / 48-hour hold on /reserve,
 *  like enquiry, so it carries no instant promise. */
export const INSTANT_BADGE = {
  instant_book: { label: "Instant Book", icon: "⚡", title: "Reserved and confirmed the moment you book" },
};

// localStorage key for the results-header toggle ("Instant Book only"). Mirrors
// hideOnDemand: read by the search service when it builds the hub query.
export const INSTANT_ONLY_KEY = "instantOnly";
export const instantOnlyOn = () => {
  try { return localStorage.getItem(INSTANT_ONLY_KEY) === "true"; } catch (_e) { return false; }
};
